import { useEffect, useState, useCallback } from "react";
import SearchInput from "../searchInput/SearchInput";
import { useUsersStore } from "../../stores/users.store";
import { useResources } from "../../hooks/useResources";
import { usePermissions } from "../../hooks/usePermissions";
import { useToast } from "../../contexts/ToastContext";
import type { WhitelistUser } from "../../interfaces/apiInterfaces/auth";
import ContentHeader from "../contentHeader/ContentHeader";
import AddUpdateUserModal from "./AddUpdateUserModal";
import DeleteModal from "../modal/DeleteModal";
import ScrollbarArrows from "../table/ScrollbarArrows";
import Pagination from "../pagination/Pagination";
import "./UsersList.scss";
import "../../assets/styles/sass/modules/_table.scss";


const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const UsersList = () => {
  const { getResourceValue } = useResources();
  const { users, pagination, isLoading, error, loadUsers } = useUsersStore();
  const { canManageUsers } = usePermissions();
  const { showError } = useToast();

  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<WhitelistUser | null>(null);
  const [userToEdit, setUserToEdit] = useState<WhitelistUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load users when filters change
  useEffect(() => {
    loadUsers({
      page_number: currentPage,
      page_size: pageSize,
      search_text: searchText || undefined,
    });
  }, [currentPage, pageSize, searchText, loadUsers]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleAddUser = () => {
    setUserToEdit(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: WhitelistUser) => {
    setUserToEdit(user);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (user: WhitelistUser) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      const { deleteUser } = useUsersStore.getState();
      await deleteUser(userToDelete.user_id);
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      setSearchText("");
      setSearchInput("");
    } catch (error) {
      console.error("Failed to delete user:", error);
      showError(
        getResourceValue("error", "Error"),
        getResourceValue("delete_user_failed", "Failed to delete user. Please try again.")
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDisciplines = useCallback((disciplines: WhitelistUser["disciplines"]) => {
    if (!disciplines || disciplines.length === 0) return "-";
    return disciplines.map((d) => d.discipline_title).join(", ");
  }, []);

  return (
    <div className="page">
      <ContentHeader
        title={getResourceValue("users", "Users")}
        headingLevel={1}
        actions={
          canManageUsers ? (
            <>
              <button className="btn btn-green" onClick={handleAddUser}>
                <i className="f-icon plus-icon"></i>
                {getResourceValue("add_user", "Add User")}
              </button>
            </>
          ) : undefined
        }
        className="content-header--users-header"
      />

      {error && <div className="error-message">{error}</div>}

      <section className="table-container">

        <div className="table-wrapper">
          <div className="table-filter-container">
            <div className="search-section">
              <SearchInput
                placeholder={getResourceValue("search_placeholder", "Search by email or name")}
                value={searchInput}
                onChange={setSearchInput}
                onKeyDown={() => {
                  setSearchText(searchInput.trim());
                }}
              />
            </div>



            {pagination && (
              <div className="sorting-section">
                <div className="sorting-controls">
                  <Pagination
                    currentPage={pagination.page_number}
                    totalPages={pagination.total_pages}
                    totalRecords={pagination.total_records}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                  />
                </div>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="loading-users">
              {getResourceValue("loading_users", "Loading users...")}
            </div>
          ) : (
            <>
              <ScrollbarArrows
                targetId="users-table-scroll"
                tablesScroll={7}
              />
              <div className="table-scroll-container" id="users-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{getResourceValue("user_id", "User ID")}</th>
                      <th>{getResourceValue("first_name", "First Name")}</th>
                      <th>{getResourceValue("last_name", "Last Name")}</th>
                      <th>{getResourceValue("email", "Email")}</th>
                      <th>{getResourceValue("disciplines", "Disciplines")}</th>
                      <th>{getResourceValue("role", "Role")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id}>
                         <td>{user.user_id}</td>
                         <td>{user.user_first_name}</td>
                         <td>{user.user_last_name}</td>                    
                        <td>{user.user_email}</td>                     
                        <td className="text-ellipsis">
                          <span className="user-disciplines-text">
                            {formatDisciplines(user.disciplines)}
                          </span>
                        </td>
                        <td className="text-ellipsis">
                          {user.roles.map((role) => (
                            <span
                              key={role.role_id}
                              className="user-role"
                            >
                              {role.role_name}
                            </span>
                          ))}
                        </td>
                        <td>
                          {canManageUsers && (
                            <div className="table-actions">
                              <button
                                className="btn btn-white"
                                onClick={() => handleEditUser(user)}
                              >
                                <i className="f-icon edit-icon"></i>
                                {getResourceValue("edit", "Edit")}
                              </button>
                              <button
                                className="btn btn-red"
                                onClick={() => handleDeleteClick(user)}
                              >
                                <i className="f-icon trash-icon"></i>
                                {getResourceValue("delete", "Delete")}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {users.length === 0 && !isLoading && (
                <div className="no-users">
                  {getResourceValue("no_users_found", "No users found.")}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <AddUpdateUserModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setUserToEdit(null);
        }}
        onSuccess={() => {
          setSearchText("");
          setSearchInput("");
        }}
        user={userToEdit}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={getResourceValue("delete_user", "Delete User")}
        message={getResourceValue(
          "confirm_delete_user",
          "Are you sure you want to delete this user?"
        )}
        isLoading={isDeleting}
      />
    </div >
  );
};

export default UsersList;
